import { Col } from 'antd';
import FormattedCurrencyNumber from 'common/formatted-currency-number/formatted-currency-number';
import UserStatusDropdown from 'common/reference-data/containers/user-status-dropdown-container';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import styled from 'styled-components';
import {
  DatePicker,
  Dropdown,
  FormItem,
  FormText,
  Input,
  InputNumber,
  Row,
  SapId,
  SubTitle,
  TimePicker,
} from 'ui';
import { indicators, orderStatus, typesWithFixedPrice } from 'utils/app-configs';
import moment from 'utils/cops-moment';
// eslint-disable-next-line
import ReferenceDataDropdown from 'common/reference-data/containers/reference-data-dropdown-container';
import { isToggleEnabled } from 'react-awesome-feature-toggles';
import { referenceTypes } from '../../../utils/app-configs';
import WipCodes from './wip-codes';

const StyledFormItem = styled(FormItem)`
  .ant-legacy-form-item-required {
    display: none;
  }
  .ant-legacy-form-item-required::before {
    display: none;
  }
`;

const StyledDatePicker = styled(DatePicker)`
  .ant-picker-input > input {
    padding-left: 0px !important;
  }
`;

const CenterFormText = styled(FormText)`
  display: flex;
  align-items: center;
`;

const StyledInputNumber = styled(InputNumber)`
  width: 100%;
  padding: 0px;
`;

// eslint-disable-next-line
class General extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedSalesOffice: null,
      initialBusinessSubType: null,
    };
  }

  onStatusesChanged = (selectedStatuses) => {
    this.props.form.setFieldsValue({ statuses: selectedStatuses });
  };

  onSalesOfficeChanged = (selectedSalesOffice) => {
    const { form } = this.props;
    const { setFieldsValue, getFieldValue } = form;
    setFieldsValue({
      salesOfficeName: selectedSalesOffice?.name,
      salesOfficeId: selectedSalesOffice?.id,
    });
    this.setState({ selectedSalesOffice });

    // if the sales office changes, check if the sales group is part of the sales office.
    const salesGroup = getFieldValue('salesGroupNumber');
    if (
      !selectedSalesOffice ||
      !selectedSalesOffice.items ||
      !selectedSalesOffice.items?.find(g => g.number === salesGroup)
    ) {
      setFieldsValue({
        // eslint-disable-next-line no-undefined
        selectedSalesGroup: undefined,
      });
    }
  };

  onSalesGroupChanged = (selectedSalesGroup) => {
    const { form: { setFieldsValue } } = this.props;
    const { selectedSalesOffice } = this.state;
    const salesOffice = selectedSalesOffice?.items?.find(i => i.number === selectedSalesGroup?.key);
    setFieldsValue({
      salesGroupId: salesOffice?.id,
      salesGroupNumber: selectedSalesGroup?.key,
      salesGroupName: selectedSalesGroup?.label,
    });
  };


  onPlannerGroupChanged = (selectedPlannerGroup) => {
    const { form: { setFieldsValue } } = this.props;
    setFieldsValue({
      plannerGroupDescription: selectedPlannerGroup && selectedPlannerGroup.name,
    });
  };

  needsPO = (activeStatus, isCreatedFromScheduled) => {
    // not initialized yet
    if (!activeStatus) {
      return false;
    }

    // only required in statuses lower than executed
    if (activeStatus.id <= orderStatus.executed) {
      // if the order is created from notification/call, don't show
      return !isCreatedFromScheduled;
    }
    return false;
  };

  /* eslint-disable complexity*/
  render() {
    const {
      data: {
        statuses = [],
        assetNumber,
        name: selectedName,
        businessIndicator: selectedIndicator,
        customerPurchaseOrder: selectedPONumber,
        sellingValue: selectedSellingValue,
        sellingValueCurrency: selectedSellingValueCurrency,
        scheduledDate: selectedRequestStartDate,
        actualdate: selectedActualDate,
        basicdate: selectedBasicDate,
        internalComment: selectedInternalComment,
        additionalRemark: selectedRemark,
        projectNumber,
        freightRegime,
        isCreatedFromScheduled,
        statusInfo: activeStatus,
        id: orderId,
        costCenterName,
        isSubOrder,
        isDraft,
        businessIndicator,
        plannerGroupDescription: plannerGroupName,
        plannerGroup: plannerGroupNumber,
        plant,
        notificationNumber,
        serviceSalesOrderNumber,
        shipmentOrderNumber,
        allowSalesOfficeEdit,
        salesGroupNumber,
        salesGroupName,
        salesOfficeNumber,
        salesOfficeName,
        salesOfficeId,
        salesGroupId,
        showBusinessSubType,
        isFiscalCodeServiceCenter,
        additionalParameter,
        additionalParameterDescription,
        incoTerms,
        showIncoTerm,
        editIncoTerm,
        incoTermDescription,
        isPoReadOnly,
        contractNumber,
        quoteNo,
        visitType,
        showExemptDeclaration,
        exemptDeclaration,
      },
      form: { getFieldDecorator },
      isDisabled,
      isLoading,
      isSaving,
      setFormDirty,
      intl,
      fetchPartners,
      userStatuses,
    } = this.props;

    const isEditable = (type) => {
      if (!activeStatus) {
        return false;
      }

      // For start date & start time, one can edit up & until release
      if (type === 'startDate' || type === 'startTime') {
        return activeStatus.id <= orderStatus.released;
      }

      if (
        type === 'salesOffice' ||
        type === 'salesGroup' ||
        type === 'businessSubType' ||
        type === 'incoTerm'
      ) {
        return activeStatus.id === orderStatus.draft;
      }

      return activeStatus.id <= orderStatus.executed;
    };

    const date = moment(selectedRequestStartDate).isValid()
      ? moment(selectedRequestStartDate).utc()
      : null;

    const actualdate = moment(selectedActualDate).isValid()
      ? moment(selectedActualDate).utc()
      : null;

    const basicdate = moment(selectedBasicDate).isValid()
      ? moment(selectedBasicDate).utc()
      : null;

    const poNumberValidator = getFieldDecorator('customerPurchaseOrder', {
      rules: [
        {
          required: activeStatus && this.needsPO(activeStatus, isCreatedFromScheduled),
          message: intl.formatMessage({ id: 'rules.PO_NUMBER' }),
        },
      ],
      initialValue: selectedPONumber,
    });

    const nameValidator = getFieldDecorator('name', {
      rules: [
        { max: 40, message: intl.formatMessage({ id: 'rules.DESCRIPTION_40' }) },
        { required: true, message: intl.formatMessage({ id: 'rules.DESCRIPTION_GENERAL' }) },
      ],
      initialValue: selectedName,
    });

    const sellingValueValidator = getFieldDecorator('sellingValue', {
      rules: [
        {
          required:
            typesWithFixedPrice.includes(selectedIndicator),
          validator: (rule, value, callback) => {
            if (!typesWithFixedPrice.includes(selectedIndicator)) {
              callback();
              return;
            }
            if (value !== null && value <= 0) {
              callback(intl.formatMessage({ id: 'rules.FIX_PRICE_0' }));
              return;
            }

            // TODO: implement/check the field Jens will forsee
            if (activeStatus.id > orderStatus.draft && !value) {
              callback(intl.formatMessage({ id: 'rules.FIX_PRICE' }));
              return;
            }

            callback();
          },
        },
      ],
      initialValue: selectedSellingValue,
    });

    const sellingValueCurrencyValidator = getFieldDecorator('sellingValueCurrency', {
      rules: [
        {
          required: typesWithFixedPrice.includes(selectedIndicator),
          message: intl.formatMessage({ id: 'rules.CURRENCY' }),
        },
      ],
      initialValue: selectedSellingValueCurrency,
    });

    const startDateValidator = getFieldDecorator('startDate', {
      rules: [{ required: true, message: intl.formatMessage({ id: 'rules.START_DATE' }) }],
      initialValue: date,
    });

    const startTimeValidator = getFieldDecorator('startTime', {
      rules: [
        {
          required: true,
          message: intl.formatMessage({ id: 'rules.START_TIME' }),
        },
      ],
      initialValue: date,
    });

    const commentValidator = getFieldDecorator('comment', {
      initialValue: selectedInternalComment,
      rules: [{ max: 30, message: intl.formatMessage({ id: 'rules.INTERNAL_COMMENT' }) }],
    });

    const remarkValidator = getFieldDecorator('remark', {
      initialValue: selectedRemark,
      rules: [{ max: 30, message: intl.formatMessage({ id: 'rules.ADDITIONAL_REMARK' }) }],
    });

    const projectNumberValidator = getFieldDecorator('projectNumber', {
      initialValue: projectNumber,
    });

    const statusesValidator = getFieldDecorator('statuses', {
      initialValue: statuses || [],
    });

    const salesOfficeValidator = getFieldDecorator('salesOfficeNumber', {
      initialValue: salesOfficeNumber,
      rules: [
        {
          required: !!allowSalesOfficeEdit,
          message: intl.formatMessage({ id: 'rules.SALES_OFFICE' }),
        },
      ],
    });

    const businessSubTypeValidator = getFieldDecorator('businessSubType', {
      initialValue: additionalParameter || this.state.initialBusinessSubType,
      rules: [
        {
          required: showBusinessSubType && isFiscalCodeServiceCenter,
          message: intl.formatMessage({ id: 'rules.BUSINESS_SUB_TYPE' }),
        },
      ],
    });

    const incoTermsValidator = getFieldDecorator('incoTerms', {
      initialValue: incoTerms,
      rules: [
        {
          required: showIncoTerm && editIncoTerm,
          message: intl.formatMessage({ id: 'rules.INCO_TERMS' }),
        },
      ],
    });

    const exemptDeclarationValidator = getFieldDecorator('exemptDeclaration', {
      initialValue: exemptDeclaration,
    });

    getFieldDecorator('salesOfficeName', {
      initialValue: salesOfficeName,
    });
    getFieldDecorator('salesOfficeId', {
      initialValue: salesOfficeId,
    });
    const salesGroupValidator = getFieldDecorator('selectedSalesGroup', {
      initialValue: {
        key: salesGroupNumber || '',
        label: salesGroupName || '',
        id: salesGroupId || '',
      },
      rules: [
        {
          required: !!allowSalesOfficeEdit,
          message: intl.formatMessage({ id: 'rules.SALES_GROUP' }),
        },
      ],
    });
    getFieldDecorator('salesGroupNumber', {
      initialValue: salesGroupNumber,
    });
    getFieldDecorator('salesGroupName', {
      initialValue: salesGroupName,
    });
    getFieldDecorator('salesGroupId', {
      initialValue: salesGroupId,
    });
    const costCenterValidator = getFieldDecorator('costCenter', {
      rules: [
        {
          required:
            activeStatus && activeStatus.id > orderStatus.draft && selectedIndicator === 'CC',
          message: intl.formatMessage({ id: 'rules.COST_CENTER' }),
        },
      ],
      initialValue: costCenterName,
    });

    // Planner group decorators
    const plannerGroupValidator = getFieldDecorator('plannerGroup', {
      initialValue: plannerGroupNumber,
    });
    getFieldDecorator('plannerGroupDescription', {
      initialValue: plannerGroupName,
    });

    const disabled = isSaving || isLoading || isDisabled;

    const hasFourColumns =
      freightRegime ||
      selectedIndicator === indicators.costToCostcenter ||
      allowSalesOfficeEdit ||
      showIncoTerm ||
      contractNumber ||
      quoteNo ||
      showExemptDeclaration;

    return (
      <>
        <SubTitle>{intl.formatMessage({ id: 'common.titles.GENERAL' })}</SubTitle>
        <Row type='flex' gutter={16} marginRight={0}>
          <Col lg={hasFourColumns ? 6 : 8} md={8} sm={12} xs={24}>
            <FormItem label={intl.formatMessage({ id: 'equipment.general.DESCRIPTION' })}>
              {isEditable() ?
                nameValidator(<Input disabled={disabled} />)
                :
                <FormText>{selectedName}</FormText>
              }
            </FormItem>
            <Row type='flex' gutter={24} marginRight={0}>
              <Col lg={8}>
                <FormItem
                  label={
                    intl.formatMessage({ id: 'equipment.general.REQUESTED_START_DATE' })
                  }
                >
                  {isEditable('startDate') ?
                    startDateValidator(<StyledDatePicker disabled={disabled} allowPast={false} />)
                    :
                    <FormText>{date && date.format('L')}</FormText>
                  }
                </FormItem>
              </Col>
              <Col lg={8}>
                <FormItem label={intl.formatMessage({ id: 'equipment.general.BASIC_DATE' })}>
                  {isEditable('startDate') ?
                    <CenterFormText>{basicdate?.format('L')}</CenterFormText>
                    :
                    <FormText>{basicdate?.format('L')}</FormText>
                  }
                </FormItem>
              </Col>
              <Col lg={8}>
                <FormItem label={intl.formatMessage({ id: 'equipment.general.ACTUAL_DATE' })}>
                  {isEditable('startDate') ?
                    <CenterFormText>{actualdate?.format('L')}</CenterFormText>
                    :
                    <FormText>{actualdate?.format('L')}</FormText>
                  }
                </FormItem>
              </Col>
            </Row>
            <FormItem
              label={intl.formatMessage({ id: 'equipment.general.REQUESTED_START_TIME' })}
            >
              {isEditable('startTime') ?
                startTimeValidator(
                  <TimePicker
                    allowClear={false}
                    showSecond={false}
                    format='HH:mm'
                    disabled={disabled}
                    minuteStep={15}
                  />,
                )
                :
                <FormText>{date && date.format('LT')}</FormText>
              }
            </FormItem>
            {isEditable() && !isPoReadOnly ?
              this.needsPO(activeStatus, isCreatedFromScheduled) &&
              <FormItem label={intl.formatMessage({ id: 'equipment.general.PO_NUMBER' })}>
                {poNumberValidator(<Input disabled={disabled || isSubOrder} />)}
              </FormItem>
              :
              <FormItem label={intl.formatMessage({ id: 'equipment.general.PO_NUMBER' })}>
                <FormText>{selectedPONumber}</FormText>
              </FormItem>
            }
            {businessIndicator === indicators.costToProject &&
              <FormItem label={intl.formatMessage({ id: 'equipment.general.PROJECT_NUMBER' })}>
                {isDraft ?
                  projectNumberValidator(<Input disabled={disabled} />)
                  :
                  <FormText>{projectNumber}</FormText>
                }
              </FormItem>
            }
            {businessIndicator === indicators.asset &&
              <FormItem label={intl.formatMessage({ id: 'equipment.general.ASSET_NUMBER' })}>
                <FormText>{assetNumber}</FormText>
              </FormItem>
            }
            {typesWithFixedPrice.includes(selectedIndicator) &&
              <span>
                {isEditable() ?
                  <Row gutter={16}>
                    <Col span={12}>
                      <FormItem label={intl.formatMessage({ id: 'equipment.general.FIX_PRICE' })}>
                        {sellingValueValidator(
                          <StyledInputNumber
                            disabled={disabled}
                            min={0}
                          />,
                        )}
                      </FormItem>
                    </Col>
                    <Col span={12}>
                      <StyledFormItem label='currency'>
                        {sellingValueCurrencyValidator(
                          <ReferenceDataDropdown
                            disabled={disabled}
                            type='order'
                            name='currencies'
                            valueLabel='name'
                            valueKey='id'
                            urlParams={{ orderId }}
                          />,
                        )}
                      </StyledFormItem>
                    </Col>
                  </Row>
                  :
                  <FormItem label={intl.formatMessage({ id: 'equipment.general.FIX_PRICE' })}>
                    <FormText>
                      <FormattedCurrencyNumber
                        value={selectedSellingValue}
                        // eslint-disable-next-line
                        style='currency'
                        currency={selectedSellingValueCurrency || 'EUR'}
                      />
                    </FormText>
                  </FormItem>
                }
              </span>
            }
            {isToggleEnabled('wipCodes') ?
              <WipCodes
                orderId={orderId}
                isDisabled={disabled || !isEditable()}
              /> : null}
          </Col>
          <Col lg={hasFourColumns ? 6 : 8} md={8} sm={12} xs={24}>
            <FormItem label={intl.formatMessage({ id: 'equipment.general.PLANNERGROUP' })}>
              {isEditable() ?
                plannerGroupValidator(<ReferenceDataDropdown
                  placeholder={intl.formatMessage({
                    id: 'equipment.organisation.SELECT_PLANNER_GROUP',
                  })}
                  onObjectChanged={this.onPlannerGroupChanged}
                  disabled={disabled}
                  type='order'
                  name='plannerGroups'
                  valueLabel='name'
                  valueKey='number'
                  urlParams={{ plant }}
                />)
                :
                <FormText>{plannerGroupName || plannerGroupNumber}</FormText>
              }
            </FormItem>
            <FormItem label={intl.formatMessage({ id: 'equipment.general.USER_STATUS' })}>
              {isEditable() ?
                statusesValidator(
                  <UserStatusDropdown
                    type='order'
                    onStatusesChanged={this.onStatusesChanged}
                    originalStatuses={statuses}
                    disabled={disabled}
                    setFormDirty={setFormDirty}
                    activeStatus={activeStatus}
                    userStatuses={userStatuses}
                  />,
                )
                :
                <FormText>{statuses && statuses?.map(s => s.id).join(', ')}</FormText>
              }
            </FormItem>
            <FormItem label={intl.formatMessage({ id: 'equipment.general.INTERNAL_COMMENT' })}>
              {isEditable() ?
                commentValidator(<Input disabled={disabled} />)
                :
                <FormText>{selectedInternalComment}</FormText>
              }
            </FormItem>
            <FormItem label={intl.formatMessage({ id: 'equipment.general.ADDITIONAL_REMARK' })}>
              {isEditable() ?
                remarkValidator(<Input disabled={disabled} />)
                :
                <FormText>{selectedRemark}</FormText>
              }
            </FormItem>
          </Col>
          <Col lg={hasFourColumns ? 6 : 8} md={8} sm={12} xs={24}>
            <FormItem
              data-test-id='order-business-indicator'
              label={intl.formatMessage({ id: 'equipment.general.INDICATOR' })}
              visible='true'
            >
              <FormText>{selectedIndicator}</FormText>
            </FormItem>
            {showBusinessSubType &&
              <FormItem
                label={intl.formatMessage({ id: 'equipment.general.BUSINESS_SUB_TYPE' })}
              >
                {isEditable('businessSubType') ?
                  businessSubTypeValidator(
                    <ReferenceDataDropdown
                      placeholder={intl.formatMessage({
                        id: 'equipment.general.BUSINESS_SUB_TYPE',
                      })}
                      urlParams={{ orderId }}
                      disabled={disabled || !isFiscalCodeServiceCenter}
                      type='order'
                      name='businessSubTypes'
                      valueLabel='description'
                      valueKey='additionalParameter'
                      setDefaultValue={value => () => {
                        this.setState({
                          initialBusinessSubType: value,
                        });
                      }}
                    />,
                  )
                  :
                  <FormText>
                    {/*
                    Sometimes we receive a translation key instead of the translated description.
                    we try to translate the key, if it fails we show the description (key).
                    //TODO receive a key, description, or an indication what we receive
                    */}
                    {additionalParameterDescription &&
                      intl.formatMessage({ id: additionalParameterDescription })}
                  </FormText>
                }
              </FormItem>
            }
            {visitType &&
              <FormItem
                label={intl.formatMessage({ id: 'equipment.general.VISIT_TYPE' })}
                visible='true'
              >
                <FormText>{visitType}</FormText>
              </FormItem>
            }
            <FormItem
              label={intl.formatMessage({ id: 'equipment.general.NOTIFICATION_NUMBER' })}
              visible='true'
            >
              <SapId originalId={notificationNumber}>
                {id => <FormText>{id}</FormText>}
              </SapId>
            </FormItem>
            <FormItem
              label={intl.formatMessage({ id: 'equipment.general.SERVICE_SALES_ORDER' })}
              visible='true'
            >
              <SapId originalId={serviceSalesOrderNumber}>
                {id => <FormText>{id}</FormText>}
              </SapId>
            </FormItem>
            <FormItem
              label={intl.formatMessage({ id: 'equipment.general.ADVANCED_SHIPMENT_SALES_ORDER' })}
              visible='true'
            >
              <SapId originalId={shipmentOrderNumber}>
                {id => <FormText>{id}</FormText>}
              </SapId>
            </FormItem>
          </Col>
          {
            hasFourColumns ?
              <Col lg={6} md={8} sm={12} xs={24}>
                {selectedIndicator === indicators.costToCostcenter &&
                  costCenterValidator(
                    <FormItem label={intl.formatMessage({ id: 'equipment.general.COSTCENTER' })}>
                      <FormText>{costCenterName}</FormText>
                    </FormItem>,
                  )}
                {freightRegime &&
                  <FormItem label={intl.formatMessage({ id: 'equipment.general.SHIP_BILL_DESC' })}>
                    <FormText>{freightRegime}</FormText>
                  </FormItem>
                }
                {contractNumber &&
                  <FormItem
                    label={intl.formatMessage({ id: 'equipment.general.CONTRACT_NUMBER' })}
                    visible='true'
                  >
                    <SapId originalId={contractNumber}>
                      {sapId => <FormText>{sapId}</FormText>}
                    </SapId>
                  </FormItem>
                }
                {quoteNo &&
                  <FormItem
                    label={intl.formatMessage({ id: 'equipment.general.QUOTE_NUMBER' })}
                  >
                    <FormText>{quoteNo}</FormText>
                  </FormItem>
                }
                {allowSalesOfficeEdit &&
                  <>
                    <FormItem
                      label={intl.formatMessage({ id: 'equipment.organisation.SALES_OFFICE' })}
                    >
                      {isEditable('salesOffice') ?
                        salesOfficeValidator(
                          <ReferenceDataDropdown
                            placeholder={intl.formatMessage({
                              id: 'equipment.organisation.SELECT_SALES_OFFICE',
                            })}
                            onObjectChanged={this.onSalesOfficeChanged}
                            disabled={disabled}
                            type={referenceTypes.equipment}
                            name='salesOffices'
                            valueLabel='name'
                            valueKey='number'
                          />,
                        )
                        :
                        <FormText>{salesOfficeName || salesOfficeNumber}</FormText>
                      }
                    </FormItem>
                    <FormItem
                      label={intl.formatMessage({ id: 'equipment.organisation.SALES_GROUP' })}
                    >
                      {isEditable('salesGroup') ?
                        salesGroupValidator(
                          <Dropdown
                            placeholder={intl.formatMessage({
                              id: 'equipment.organisation.SELECT_SALES_GROUP',
                            })}
                            onChange={this.onSalesGroupChanged}
                            values={this.state.selectedSalesOffice ?
                              this.state.selectedSalesOffice.items :
                              []
                            }
                            disabled={disabled || !this.state.selectedSalesOffice}
                            valueLabel='name'
                            valueKey='number'
                            labelInValue={true}
                          />,
                        )
                        :
                        <FormText>{salesGroupName || salesGroupNumber}</FormText>
                      }
                    </FormItem>
                  </>
                }
                {showIncoTerm &&
                  <FormItem
                    label={intl.formatMessage({ id: 'equipment.general.INCO_TERMS' })}
                  >
                    {isEditable('incoTerm') ?
                      incoTermsValidator(
                        <ReferenceDataDropdown
                          placeholder={intl.formatMessage({
                            id: 'equipment.general.INCO_TERMS',
                          })}
                          urlParams={{ orderId }}
                          disabled={disabled || !editIncoTerm}
                          type='order'
                          name='incoTerms'
                          valueLabel='description'
                          valueKey='id'
                          onChange={fetchPartners}
                        />,
                      )
                      :
                      <FormText>{incoTermDescription}</FormText>
                    }
                  </FormItem>
                }
                {showExemptDeclaration && isToggleEnabled('taxExempts') &&
                  <FormItem
                    label={intl.formatMessage({ id: 'equipment.general.EXEMPT_DECLARATION' })}
                  >
                    {exemptDeclarationValidator(
                      <ReferenceDataDropdown
                        placeholder={intl.formatMessage({
                          id: 'equipment.general.EXEMPT_DECLARATION',
                        })}
                        disabled={disabled}
                        allowClear={true}
                        type='order'
                        name='taxExempts'
                        valueLabel='description'
                        valueKey='id'
                        listHeight={320}
                      />,
                    )}
                  </FormItem>
                }
              </Col> :
              null
          }
        </Row>
      </>
    );
  }
}

General.propTypes = {
  isLoading: PropTypes.bool,
  isSaving: PropTypes.bool,
  form: PropTypes.object,
  isDisabled: PropTypes.bool,
  intl: PropTypes.object,
  setFormDirty: PropTypes.func,
  data: PropTypes.object,
  fetchPartners: PropTypes.func,
  userStatuses: PropTypes.array,
};

export default injectIntl(General);
